import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, File as IconoArchivo } from 'lucide-react';
import { CORREO_URL } from '../../../lib/dominios';
import { esPregunta, formatoFecha, tamanoLegible } from '../../../lib/formularios';

/*
 * Pestaña "Pregunta": se elige una pregunta y se ven todas sus respuestas distintas, cuántas veces
 * aparece cada una y en qué respuestas (el número lleva a la vista Individual de esa respuesta).
 * Se calcula en el cliente a partir de todas las respuestas: el resumen del servidor agrupa, pero
 * no dice QUIÉN contestó qué.
 */

function agrupar(item, respuestas) {
  const grupos = new Map();
  const sumar = (clave, texto, indice, numero) => {
    if (!grupos.has(clave)) grupos.set(clave, { clave, texto, entradas: [] });
    grupos.get(clave).entradas.push({ indice, numero });
  };
  const opcion = (id) => (item.opciones || []).find((o) => o.id === id)?.texto ?? '(opción eliminada)';

  respuestas.forEach((r, indice) => {
    const v = r.respuestas?.[item.id];
    if (v === undefined || v === null) return;
    switch (item.tipo) {
      case 'opcion_multiple':
      case 'desplegable':
        if (v.opcion) sumar(`o:${v.opcion}`, opcion(v.opcion), indice, r.numero);
        else if (v.otro) sumar(`t:${v.otro}`, `Otro: ${v.otro}`, indice, r.numero);
        break;
      case 'casillas':
        (v.opciones || []).forEach((id) => sumar(`o:${id}`, opcion(id), indice, r.numero));
        if (v.otro) sumar(`t:${v.otro}`, `Otro: ${v.otro}`, indice, r.numero);
        break;
      case 'cuadricula_opcion':
      case 'cuadricula_casillas':
        (item.filas || []).forEach((f) => {
          const cols = Array.isArray(v[f.id]) ? v[f.id] : (v[f.id] ? [v[f.id]] : []);
          cols.forEach((c) => {
            const col = (item.columnas || []).find((x) => x.id === c)?.texto || '';
            sumar(`g:${f.id}:${c}`, `${f.texto}: ${col}`, indice, r.numero);
          });
        });
        break;
      case 'archivo':
        (v || []).forEach((a) => {
          if (!grupos.has(`a:${a.id}`)) grupos.set(`a:${a.id}`, { clave: `a:${a.id}`, texto: a.nombre, archivo: a, entradas: [] });
          grupos.get(`a:${a.id}`).entradas.push({ indice, numero: r.numero });
        });
        break;
      case 'fecha':
        sumar(`v:${v}`, formatoFecha(v), indice, r.numero);
        break;
      default: {
        const texto = String(v);
        // Mayúsculas y espacios de más no hacen dos respuestas distintas
        sumar(`v:${texto.trim().toLowerCase()}`, texto.trim(), indice, r.numero);
      }
    }
  });

  const lista = [...grupos.values()];
  if (['opcion_multiple', 'desplegable', 'casillas'].includes(item.tipo)) {
    // Las opciones en su orden del formulario, luego las de "Otro" por frecuencia
    const orden = (item.opciones || []).map((o) => `o:${o.id}`);
    lista.sort((a, b) => {
      const ia = orden.indexOf(a.clave);
      const ib = orden.indexOf(b.clave);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return b.entradas.length - a.entradas.length;
    });
  } else if (['escala', 'calificacion'].includes(item.tipo)) {
    lista.sort((a, b) => Number(a.texto) - Number(b.texto));
  } else if (item.tipo !== 'archivo' && !item.tipo.startsWith('cuadricula')) {
    lista.sort((a, b) => b.entradas.length - a.entradas.length);
  }
  return lista;
}

export default function VistaPregunta({ formulario, respuestas, onVerRespuesta }) {
  const preguntas = useMemo(() => (formulario?.items || []).filter(esPregunta), [formulario]);
  const [indice, setIndice] = useState(0);
  const item = preguntas[Math.min(indice, preguntas.length - 1)];
  const grupos = useMemo(() => (item ? agrupar(item, respuestas || []) : []), [item, respuestas]);

  if (!preguntas.length) return <p className="rs-vacio">Este formulario todavía no tiene preguntas.</p>;

  const respondidas = new Set(grupos.flatMap((g) => g.entradas.map((e) => e.indice))).size;

  return (
    <div className="rs-vista-pregunta">
      <div className="rs-tarjeta rs-selector-pregunta">
        <button
          type="button"
          className="od-tool-btn"
          onClick={() => setIndice((i) => Math.max(0, i - 1))}
          disabled={indice === 0}
          aria-label="Pregunta anterior"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <select
          className="rs-select"
          value={indice}
          onChange={(e) => setIndice(Number(e.target.value))}
          aria-label="Pregunta"
        >
          {preguntas.map((p, i) => (
            <option key={p.id} value={i}>{`${i + 1}. ${p.titulo || 'Pregunta sin título'}`}</option>
          ))}
        </select>
        <span className="rs-selector-contador">{indice + 1} de {preguntas.length}</span>
        <button
          type="button"
          className="od-tool-btn"
          onClick={() => setIndice((i) => Math.min(preguntas.length - 1, i + 1))}
          disabled={indice >= preguntas.length - 1}
          aria-label="Pregunta siguiente"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <section className="rs-tarjeta" aria-labelledby="rs-pregunta-actual">
        <h3 className="rs-tarjeta-titulo" id="rs-pregunta-actual">{item.titulo || 'Pregunta sin título'}</h3>
        <div className="rs-tarjeta-meta">{respondidas} {respondidas === 1 ? 'respuesta' : 'respuestas'}</div>
        {!grupos.length && <p className="rs-vacio">Nadie ha respondido esta pregunta.</p>}
        <ul className="rs-grupos">
          {grupos.map((g) => (
            <li key={g.clave} className="rs-grupo">
              <div className="rs-grupo-cabeza">
                {g.archivo ? (
                  <a className="rs-archivo" href={`${CORREO_URL}/file/${g.archivo.id}`} target="_blank" rel="noopener noreferrer">
                    <IconoArchivo className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="rs-archivo-nombre">{g.texto}</span>
                    <span className="rs-archivo-tamano">{tamanoLegible(g.archivo.tamano)}</span>
                  </a>
                ) : (
                  <span className="rs-grupo-texto">{g.texto || '(vacío)'}</span>
                )}
                <span className="rs-grupo-n">{g.entradas.length} {g.entradas.length === 1 ? 'respuesta' : 'respuestas'}</span>
              </div>
              <div className="rs-grupo-numeros" aria-label="Respuestas">
                {g.entradas.map((e) => (
                  <button
                    key={`${g.clave}-${e.indice}`}
                    type="button"
                    className="rs-numero"
                    onClick={() => onVerRespuesta?.(e.indice)}
                    title={`Ver la respuesta #${e.numero}`}
                  >
                    #{e.numero}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
